import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Newspaper } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/blog-posts").then((res) => {
      if (!cancelled) setPosts(res.data || []);
    }).catch(() => {
      if (!cancelled) setError(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">
      <nav className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </nav>

      <header className="max-w-3xl mx-auto px-6 pt-20 pb-12 text-center">
        <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">
          <Newspaper className="w-3.5 h-3.5" /> Blog
        </span>
        <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight text-gray-900 mb-4">
          News, updates &amp; ideas
        </h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">
          Product news, playbooks, and lessons from the team building CombinedWork.
        </p>
      </header>

      <section className="max-w-5xl mx-auto px-6 pb-24">
        {loading && (
          <p className="text-center text-sm text-gray-400 py-16" data-testid="blog-loading">Loading posts…</p>
        )}
        {!loading && error && (
          <p className="text-center text-sm text-gray-400 py-16">Couldn't load posts right now — please try again later.</p>
        )}
        {!loading && !error && posts.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-16" data-testid="blog-empty">No posts published yet — check back soon.</p>
        )}
        {!loading && !error && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
            {posts.map((p) => (
              <Link
                key={p.post_id}
                to={`/blog/${p.slug}`}
                className="group border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg hover:shadow-gray-100 transition-all flex flex-col"
                data-testid={`blog-post-card-${p.slug}`}
              >
                {p.cover_image ? (
                  <img src={p.cover_image} alt="" className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-violet-50 grid place-items-center">
                    <Newspaper className="w-8 h-8 text-violet-200" strokeWidth={1.5} />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {p.published_at && (
                    <span className="text-xs text-gray-400 mb-2">{new Date(p.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  )}
                  <h2 className="font-semibold text-gray-900 text-base mb-2 group-hover:text-violet-600 transition-colors">{p.title}</h2>
                  {p.excerpt && <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>}
                  <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-violet-600">
                    Read more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-gray-100 py-8 px-6 text-center">
        <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} CombinedWork. All rights reserved.</span>
      </footer>
    </div>
  );
}
